import { DEFAULT_IMPORT_TARIFF_PENCE } from "./helioScoreFunnel";

/**
 * Display-only "do nothing" grid-cost projection for the quote funnel.
 *
 * Formula matches App\Services\Proposal\ProposalCalculator::computeDoNothing:
 * yearN annual = spend × (1 + inflation)^(N-1)
 *
 * Inflation bands match ProposalContractMapper::defaultFinancialAssumptions()
 * when proposal_schema.calculation.electricity_inflation_pct is 5
 * (expected 5%, conservative 3%, optimised 7%). If that config changes,
 * update DO_NOTHING_INFLATION. Persisted proposal figures stay in Laravel.
 */
export const DO_NOTHING_INFLATION = {
  expected: 0.05,
  conservative: 0.03,
  optimised: 0.07,
} as const;

export interface DoNothingProjection {
  year1Annual: number;
  year10Annual: number;
  year25Annual: number;
  year10Cumulative: number;
  year25Cumulative: number;
}

export type DoNothingScenarios = {
  expected: DoNothingProjection;
  conservative: DoNothingProjection;
  optimised: DoNothingProjection;
};

/**
 * "Do nothing" projection — grid electricity cost with no solar/battery,
 * usage held constant, escalated at the given inflation rate.
 */
export function computeDoNothing(
  annualSpend: number,
  inflation: number,
  years = 25,
): DoNothingProjection {
  let cum = 0;
  const annualByYear: number[] = [0];
  const cumByYear: number[] = [0];

  for (let y = 1; y <= years; y++) {
    const yearly = annualSpend * (1 + inflation) ** (y - 1);
    annualByYear[y] = yearly;
    cum += yearly;
    cumByYear[y] = cum;
  }

  return {
    year1Annual: annualByYear[1],
    year10Annual: annualByYear[10],
    year25Annual: annualByYear[25],
    year10Cumulative: cumByYear[10],
    year25Cumulative: cumByYear[25],
  };
}

export function computeAllDoNothing(annualSpend: number): DoNothingScenarios {
  return {
    expected: computeDoNothing(annualSpend, DO_NOTHING_INFLATION.expected),
    conservative: computeDoNothing(annualSpend, DO_NOTHING_INFLATION.conservative),
    optimised: computeDoNothing(annualSpend, DO_NOTHING_INFLATION.optimised),
  };
}

/** Resolve annual spend for projections: bill spend first, else kWh × tariff. */
export function resolveAnnualSpendGbp(opts: {
  annualSpendGbp?: number | null;
  annualKwh?: number | null;
  tariffPencePerKwh?: number | null;
}): number | null {
  if (opts.annualSpendGbp != null && opts.annualSpendGbp > 0) {
    return opts.annualSpendGbp;
  }
  const kwh = opts.annualKwh ?? 0;
  if (kwh <= 0) return null;
  const pence = opts.tariffPencePerKwh != null && opts.tariffPencePerKwh > 0
    ? opts.tariffPencePerKwh
    : DEFAULT_IMPORT_TARIFF_PENCE;
  return kwh * (pence / 100);
}
